import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import {
  QAProcessorConfig,
  updateProcessorConfig
} from 'services/qaprocessor.service';

interface Props {
  show: boolean;
  onHide: () => void;
  config: QAProcessorConfig;
  onConfigUpdate: () => void;
}

export const QAProcessorConfigModal: React.FC<Props> = ({
  show,
  onHide,
  config,
  onConfigUpdate
}) => {
  const [isActive, setIsActive] = useState(config.active);
  const [lastProcessedDate, setLastProcessedDate] = useState<Date | null>(
    config.lastProcessedDate ? new Date(config.lastProcessedDate) : null
  );
  // eslint-disable-next-line
  const [maxConcurrent, setMaxConcurrent] = useState(config.maxConcurrentProcesses);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transcriptionMainProvider, setTranscriptionMainProvider] = useState(
    config.transcriptionMainProvider
  );
  const [transcriptionFallbackProvider, setTranscriptionFallbackProvider] =
    useState(config.transcriptionFallbackProvider);
  const [analysisMainProvider, setAnalysisMainProvider] = useState(
    config.analysisMainProvider
  );
  const [analysisFallbackProvider, setAnalysisFallbackProvider] = useState(
    config.analysisFallbackProvider
  );

  useEffect(() => {
    setIsActive(config.active);
    // eslint-disable-next-line
    setLastProcessedDate(config.lastProcessedDate ? new Date(config.lastProcessedDate) : null);
    setMaxConcurrent(config.maxConcurrentProcesses);
  }, [config]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await updateProcessorConfig({
        active: isActive,
        lastProcessedDate: lastProcessedDate?.toISOString() || null,
        maxConcurrentProcesses: maxConcurrent,
        transcriptionMainProvider,
        transcriptionFallbackProvider,
        analysisMainProvider,
        analysisFallbackProvider
      });
      onConfigUpdate();
      onHide();
    } catch (error) {
      console.error('Error updating config:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>QA Processor Configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-5">
        <Form>
          <Form.Group className="mb-3 mt-2">
            <Form.Check
              type="switch"
              id="processor-active"
              label="Processor Active"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Max Concurrent Processes</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={20}
              value={maxConcurrent}
              onChange={e => setMaxConcurrent(parseInt(e.target.value))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Transcription Providers</Form.Label>
            <div className="d-flex gap-3 align-items-start">
              <div className="flex-fill">
                <Form.Select
                  value={transcriptionMainProvider}
                  onChange={e => setTranscriptionMainProvider(e.target.value)}
                >
                  <option value="AzureSpeechBatch1">
                    Azure Speech Batch 1
                  </option>
                  <option value="AzureSpeechBatch2">
                    Azure Speech Batch 2
                  </option>
                  <option value="AssemblyAI1">AssemblyAI 1</option>
                  <option value="AssemblyAI2">AssemblyAI 2</option>
                </Form.Select>
                <Form.Text className="text-primary px-3">Main</Form.Text>
              </div>
              <div className="d-flex align-items-center">
                <span className="mx-2">→</span>
              </div>
              <div className="flex-fill">
                <Form.Select
                  value={transcriptionFallbackProvider}
                  onChange={e =>
                    setTranscriptionFallbackProvider(e.target.value)
                  }
                >
                  <option value="AzureSpeechBatch1">
                    Azure Speech Batch 1
                  </option>
                  <option value="AzureSpeechBatch2">
                    Azure Speech Batch 2
                  </option>
                  <option value="AssemblyAI1">AssemblyAI 1</option>
                  <option value="AssemblyAI2">AssemblyAI 2</option>
                </Form.Select>
                <Form.Text className="text-primary px-3">Fallback</Form.Text>
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Analysis Providers</Form.Label>
            <div className="d-flex gap-3 align-items-start">
              <div className="flex-fill">
                <Form.Select
                  value={analysisMainProvider}
                  onChange={e => setAnalysisMainProvider(e.target.value)}
                >
                  <option value="AzureOpenAI1">Azure OpenAI 1</option>
                  <option value="AzureOpenAI2">Azure OpenAI 2</option>
                  <option value="AssemblyAI1">AssemblyAI 1</option>
                  <option value="AssemblyAI2">AssemblyAI 2</option>
                </Form.Select>
                <Form.Text className="text-primary px-3">Main</Form.Text>
              </div>
              <div className="d-flex align-items-center">
                <span className="mx-2">→</span>
              </div>
              <div className="flex-fill">
                <Form.Select
                  value={analysisFallbackProvider}
                  onChange={e => setAnalysisFallbackProvider(e.target.value)}
                >
                  <option value="AzureOpenAI1">Azure OpenAI 1</option>
                  <option value="AzureOpenAI2">Azure OpenAI 2</option>
                  <option value="AssemblyAI1">AssemblyAI 1</option>
                  <option value="AssemblyAI2">AssemblyAI 2</option>
                </Form.Select>
                <Form.Text className="text-primary px-3">Fallback</Form.Text>
              </div>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Last Processed Date</Form.Label>
            <DatePicker
              value={lastProcessedDate ? [lastProcessedDate] : []}
              onChange={dates => setLastProcessedDate(dates[0] || null)}
              options={{
                dateFormat: 'Y-m-d H:i',
                enableTime: true
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
